import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';
import { resolveActiveLockInvestor } from '@/utils/investorResolution';
import { lockCalendarLabel, withLockCalendarDates } from '@/services/lockCalendarDates';
import type { LockVersion, LoanPricingItem } from '@/services/loanPricingService';
import { readEdgeFunctionErrorBody as readActivationErrorBody } from '@/services/edgeFunctionErrorBody';

const log = createComponentLogger('DeliveryLockService', 'delivery-lock');

export type PricingSourceType = 'encompass_current' | 'delivery_lock';

export interface DeliveryLockSelection {
  id: string;
  organizationId: string;
  encompassLoanId: string;
  loanId: string | null;
  lockVersionId: string;
  lockVersion: number | null;
  lockVersionLabel: string | null;
  lockVersionCapturedAt: string | null;
  lockVersionTriggerReason: string | null;
  /**
   * Legacy compatibility fields. Existing downstream types still use these
   * names, but Product & Pricing now treats `lockVersionId` as canonical.
   */
  lockSnapshotId: string;
  snapshotVersion: number | null;
  snapshotLabel: string | null;
  snapshotCapturedAt: string | null;
  snapshotTriggerReason: string | null;
  active: boolean;
  rateLockFields: Record<string, unknown>;
  pricingContext: Record<string, unknown>;
  pricingItemsSnapshot: LoanPricingItem[];
  pricingOverrides: Record<string, unknown>;
  selectedBy: string | null;
  selectedByEmail: string | null;
  selectedAt: string;
  updatedBy: string | null;
  updatedByEmail: string | null;
  updatedAt: string;
}

export interface ActiveDeliveryLockRow {
  id: string;
  organization_id: string;
  encompass_loan_id: string;
  loan_id: string | null;
  lock_version_id: string;
  active: boolean;
  selected_by: string | null;
  selected_by_email: string | null;
  selected_at: string;
  updated_by: string | null;
  updated_by_email: string | null;
  updated_at: string;
  lock_versions?: {
    id: string;
    version: number | null;
    label: string | null;
    trigger_reason: string | null;
    captured_at: string | null;
    rate_lock_fields: Record<string, unknown> | null;
    source_lock_snapshot_id: string | null;
  } | null;
}

export interface DeliveryLockLoanShape {
  id?: string;
  organization_id?: string;
  encompass_loan_id?: string;
  [key: string]: unknown;
}

export function mapActiveDeliveryLockRow(row: ActiveDeliveryLockRow): DeliveryLockSelection {
  const version = row.lock_versions ?? null;
  const rawRateLockFields = version?.rate_lock_fields ?? {};
  // Program-first investor normalization: rate_lock_loan_program is the
  // authoritative source of who the loan is going to at lock time. The
  // captured `investor_name` may be stale (e.g., independently edited in
  // Encompass after the lock). Re-derive on the read path; the persisted
  // JSONB is untouched. The same goes for an OB-written version's UTC
  // instants: its lock dates and label read as Pacific calendar dates.
  const rateLockFields = withLockCalendarDates({
    ...rawRateLockFields,
    investor_name: resolveActiveLockInvestor(
      (rawRateLockFields as Record<string, unknown>).rate_lock_loan_program as string | null,
      (rawRateLockFields as Record<string, unknown>).investor_name as string | null
    )
  });
  const label = lockCalendarLabel(
    version?.label,
    version?.version,
    (rawRateLockFields as Record<string, unknown>).rate_lock_buy_side_lock_date
  );
  const capturedAt = version?.captured_at ?? null;
  const triggerReason = version?.trigger_reason ?? null;
  const legacySnapshotId = version?.source_lock_snapshot_id ?? row.lock_version_id;

  return {
    id: row.id,
    organizationId: row.organization_id,
    encompassLoanId: row.encompass_loan_id,
    loanId: row.loan_id,
    lockVersionId: row.lock_version_id,
    lockVersion: version?.version ?? null,
    lockVersionLabel: label,
    lockVersionCapturedAt: capturedAt,
    lockVersionTriggerReason: triggerReason,
    lockSnapshotId: legacySnapshotId,
    snapshotVersion: version?.version ?? null,
    snapshotLabel: label,
    snapshotCapturedAt: capturedAt,
    snapshotTriggerReason: triggerReason,
    active: row.active,
    rateLockFields,
    pricingContext: {},
    pricingItemsSnapshot: [],
    pricingOverrides: {},
    selectedBy: row.selected_by,
    selectedByEmail: row.selected_by_email,
    selectedAt: row.selected_at,
    updatedBy: row.updated_by,
    updatedByEmail: row.updated_by_email,
    updatedAt: row.updated_at
  };
}

/**
 * Overlay the *lock-derived* fields from an active delivery-lock selection
 * onto a live loan row. Only `rateLockFields` (price, rate, dates, days,
 * LLPAs/margin stack, lock program, etc.) are spread — these are the fields
 * that are *selected by* choosing this delivery lock.
 *
 * Pricing-context fields (`discount_point_percent`, `origination_fee_percent`,
 * `trans_details_non_specific_lender_credit`, `le_fees`, etc.) are NOT spread:
 * they are local UI inputs, independent of which lock is currently selected,
 * and the workspace must always source them from the live `encompass_loans`
 * row so edits remain visible to downstream live readers (Banker Recon,
 * notification recipients).
 *
 * The captured `selection.pricingContext` is preserved on the selection row
 * for the rate-lock confirmation email Edge Function — that consumer keeps
 * its captured-time semantics.
 *
 * `LIVE_ONLY_LOAN_FIELDS` are never overlaid, even though lock versions store
 * them: the Investor Loan # (Encompass 352) is entered after the lock, so a
 * lock copy holds a blank or an earlier number. Lock history keeps its copy.
 */
const LIVE_ONLY_LOAN_FIELDS = ['rate_lock_investor_commitment'] as const;

export function applyDeliveryLockToLoan<T extends DeliveryLockLoanShape>(
  loan: T,
  selection: DeliveryLockSelection | null | undefined
): T {
  if (!selection?.active) {
    return loan;
  }
  const lockFields: Record<string, unknown> = { ...selection.rateLockFields };
  for (const key of LIVE_ONLY_LOAN_FIELDS) {
    delete lockFields[key];
  }
  return {
    ...loan,
    ...lockFields
  };
}

/**
 * Historical export shape for Banker Recon. Despite the function name, the
 * current source is active_delivery_locks -> lock_versions. The snapshot keys
 * are retained only so existing recon/audit JSON consumers do not break.
 */
export function buildDeliveryLockSourceSnapshot(selection: DeliveryLockSelection | null): Record<string, unknown> | null {
  if (!selection) {
    return null;
  }
  return {
    selectionId: selection.id,
    lockVersionId: selection.lockVersionId,
    lockVersion: selection.lockVersion,
    lockVersionLabel: selection.lockVersionLabel,
    lockVersionCapturedAt: selection.lockVersionCapturedAt,
    lockVersionTriggerReason: selection.lockVersionTriggerReason,
    lockSnapshotId: selection.lockSnapshotId,
    snapshotVersion: selection.snapshotVersion,
    snapshotLabel: selection.snapshotLabel,
    snapshotCapturedAt: selection.snapshotCapturedAt,
    snapshotTriggerReason: selection.snapshotTriggerReason,
    rateLockFields: selection.rateLockFields,
    pricingContext: selection.pricingContext,
    pricingItemsSnapshot: selection.pricingItemsSnapshot,
    pricingOverrides: selection.pricingOverrides
  };
}

export function getPricingSourceType(selection: DeliveryLockSelection | null): PricingSourceType {
  return selection?.active ? 'delivery_lock' : 'encompass_current';
}

export async function getActiveDeliveryLockSelection(
  encompassLoanId: string
): Promise<DeliveryLockSelection | null> {
  const { data, error } = await supabase
    .from('active_delivery_locks')
    .select('*, lock_versions(*)')
    .eq('encompass_loan_id', encompassLoanId)
    .eq('active', true)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    log.warn('Failed to fetch active delivery lock selection', { encompassLoanId, error: error.message });
    return null;
  }
  return data ? mapActiveDeliveryLockRow(data as ActiveDeliveryLockRow) : null;
}

/**
 * Same read as `getActiveDeliveryLockSelection`, but a query error throws
 * instead of reading as "no active lock". Used after Set Active & Save, where
 * a null would clear a selection the server may have just created.
 */
export async function getActiveDeliveryLockSelectionStrict(
  encompassLoanId: string
): Promise<DeliveryLockSelection | null> {
  const { data, error } = await supabase
    .from('active_delivery_locks')
    .select('*, lock_versions(*)')
    .eq('encompass_loan_id', encompassLoanId)
    .eq('active', true)
    .order('updated_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    throw new Error(error.message || 'Failed to load active delivery lock');
  }
  return data ? mapActiveDeliveryLockRow(data as ActiveDeliveryLockRow) : null;
}

const ACTIVATION_ERROR_COPY: Record<string, string> = {
  BELOW_PAR: 'Below par — cover the shortfall before setting active',
  PAR_CHECK_UNAVAILABLE: "Loan amount is missing, so par can't be checked.",
  FEATURE_DISABLED: 'Set Active guard is off — refresh the page.',
  ACTIVE_GROUP_IN_FLIGHT: 'Finish the pending pricing save first',
  STALE_ACTIVE_SELECTION: 'Pricing changed under you — review and try again',
  ENCOMPASS_READ_FAILED: "Couldn't read the loan's pricing from Encompass — try again.",
  BANKER_RECON_FINALIZED: 'Pricing is locked. Reopen Banker Recon before changing the delivery lock.',
  LOCK_VERSION_NOT_FOUND: 'That lock version no longer exists. Refresh and try again.',
  LOCK_VERSION_NOT_SELECTABLE: 'That lock version is read-only and cannot be set active.',
  UNAUTHORIZED: 'You are not signed in. Please refresh and try again.',
  LOAN_ACCESS_DENIED: 'You do not have access to this loan.'
};

/** Thrown by `activateDeliveryLockVersion` with the server's error code. */
export class DeliveryLockActivationError extends Error {
  readonly code: string;
  readonly shortfallPoints?: number;
  readonly shortfallDollars?: number;

  constructor(code: string, detail?: { shortfallPoints?: number; shortfallDollars?: number; message?: string }) {
    super(detail?.message ?? ACTIVATION_ERROR_COPY[code] ?? `Failed to set the lock active — ${code}`);
    this.name = 'DeliveryLockActivationError';
    this.code = code;
    this.shortfallPoints = detail?.shortfallPoints;
    this.shortfallDollars = detail?.shortfallDollars;
  }
}

/**
 * Set a lock version active through the server, which checks par against
 * that lock's economics first. Only used when `pricing_set_active_par_guard`
 * is on; the flag-off path stays on the direct RPC below.
 */
export async function activateDeliveryLockVersion(args: {
  loanId: string;
  encompassLoanId: string;
  lockVersionId: string;
  expectedActiveLockVersionId: string | null;
}): Promise<DeliveryLockSelection> {
  let data: { ok?: boolean; selectionId?: string; lockVersionId?: string } | null = null;
  try {
    const result = await supabase.functions.invoke<{ ok: boolean; selectionId: string; lockVersionId: string }>(
      'enqueue-encompass-write',
      {
        body: {
          action: 'activate-lock',
          loanId: args.loanId,
          encompassLoanId: args.encompassLoanId,
          lockVersionId: args.lockVersionId,
          expectedActiveLockVersionId: args.expectedActiveLockVersionId
        }
      }
    );
    if (result.error) {
      const parsed = await readActivationErrorBody(result.error, result.response);
      const code = typeof parsed?.error === 'string' ? parsed.error : 'ACTIVATION_FAILED';
      throw new DeliveryLockActivationError(code, {
        shortfallPoints: typeof parsed?.shortfallPoints === 'number' ? parsed.shortfallPoints : undefined,
        shortfallDollars: typeof parsed?.shortfallDollars === 'number' ? parsed.shortfallDollars : undefined
      });
    }
    data = result.data;
  } catch (e) {
    if (e instanceof DeliveryLockActivationError) {
      throw e;
    }
    log.error('activate-lock invoke threw', { metadata: { encompassLoanId: args.encompassLoanId } }, e as Error);
    throw new DeliveryLockActivationError('NETWORK_ERROR', { message: 'Could not reach the server. Try again.' });
  }
  if (!data?.ok) {
    throw new DeliveryLockActivationError('ACTIVATION_FAILED');
  }

  const selection = await getActiveDeliveryLockSelectionStrict(args.encompassLoanId);
  if (!selection || selection.lockVersionId !== args.lockVersionId) {
    throw new DeliveryLockActivationError('ACTIVATION_FAILED', {
      message: 'The lock was set active but could not be re-read. Refresh the page.'
    });
  }
  return selection;
}

export async function setActiveDeliveryLockFromVersion(args: {
  loanId: string;
  encompassLoanId: string;
  organizationId: string;
  version: LockVersion;
}): Promise<DeliveryLockSelection> {
  const { data, error } = await supabase.rpc('set_active_delivery_lock_version', {
    p_encompass_loan_id: args.encompassLoanId,
    p_loan_id: args.loanId,
    p_lock_version_id: args.version.id
  });

  if (error) {
    // Once the par guard flag is on globally the RPC refuses tabs loaded
    // before the release; the message is the cue to reload.
    if (error.message?.includes('REFRESH_REQUIRED')) {
      throw new Error('Refresh the page to set a lock active.');
    }
    throw new Error(error.message || 'Failed to set delivery lock selection');
  }

  const row = data as ActiveDeliveryLockRow;
  const { data: hydrated, error: hydrateError } = await supabase
    .from('active_delivery_locks')
    .select('*, lock_versions(*)')
    .eq('id', row.id)
    .maybeSingle();
  if (hydrateError || !hydrated) {
    throw new Error(hydrateError?.message || 'Failed to load active delivery lock');
  }
  return mapActiveDeliveryLockRow(hydrated as ActiveDeliveryLockRow);
}
